import { useEffect, useState } from 'react'
import moment from 'moment'
import ReactPixel from 'react-facebook-pixel'

import { wordpressPost } from 'helpers/wordpress'
import APPLY_QUERY from 'graphql/apply.query'

import ApplyLayout from 'components/ApplyLayout'
import ApplyFormDetails from 'components/ApplyForm/ApplyFormDetails'
import ApplyFormApplicationType from 'components/ApplyForm/ApplyFormApplicationType'
import ApplyFormGuardianConsent from 'components/ApplyForm/ApplyFormGuardianConsent'
import ApplyFormMeasurements from 'components/ApplyForm/ApplyFormMeasurements'
import ApplyFormImages from 'components/ApplyForm/ApplyFormImages'
import ApplyFormSocial from 'components/ApplyForm/ApplyFormSocial'
import ApplyFormConsent from 'components/ApplyForm/ApplyFormConsent'
import ApplyComplete from 'components/ApplyComplete'
import Overlay from 'components/Overlay'

import s from './form.module.css'
import getGlobalSettings from '../helpers/getGlobalSettings'
import getSites from '../helpers/getSites'
import { createApolloClient } from '../lib/createApolloClient'
import StateInitializer from '../components/StateInitializer'
import * as Sentry from "@sentry/nextjs";

const stepOrder = [
  'details',
  'guardianConsent',
  'applicationType',
  'measurements',
  'images',
  'social',
  'consent',
  'complete',
]

function Start({ globalSettings, sites, siteSlug }) {
  const [fieldValues, setFieldValues] = useState({})
  const [emailValidated, setEmailValidated] = useState(false)
  const [complete, setComplete] = useState(false)
  const [fetchState, setFetchState] = useState({ loading: false, error: null })


  useEffect(
    () => {

      if (fieldValues.email && !emailValidated) {
        validateEmail()
      } else if (fieldValues.consent && stepOrder[step] === 'consent') {
        sendApplication()
      } else {
        nextStep()
      }
    },
    [fieldValues],
  )

  useEffect(
    () => {
      if (emailValidated || complete) nextStep()
    },
    [emailValidated, complete],
  )

  const calculateStep = () => {
    if (!fieldValues.age || !emailValidated) return stepOrder.indexOf('details')

    if (fieldValues.age < 16 && !fieldValues.guardianConsent)
      return stepOrder.indexOf('guardianConsent')

    if (!fieldValues.applicationType) return stepOrder.indexOf('applicationType')

    if (!fieldValues.eyeColour || !fieldValues.hairColour)
      return stepOrder.indexOf('measurements')

    if (
      !fieldValues.sideOn ||
      !fieldValues.fullFigure ||
      !fieldValues.portrait ||
      !fieldValues.headAndShoulders
    )
      return stepOrder.indexOf('images')

    if (!fieldValues.socialSubmitted) return stepOrder.indexOf('social')

    if (!fieldValues.consent) return stepOrder.indexOf('consent')

    return stepOrder.indexOf('complete')
  }
  const [step, setStep] = useState(calculateStep())

  const nextStep = () => {
    let newStep = step + 1
    const maxStep = calculateStep()
    if (
      newStep === stepOrder.indexOf('guardianConsent') &&
      fieldValues.age >= 16
    ) newStep += 1
    setStep(newStep <= maxStep ? newStep : maxStep)
  }

  const handleSubmit = data => {
    const changedData = Object.keys(data).filter(
      k => fieldValues[k] !== data[k],
    )
    if (changedData.length === 0) {
      nextStep()
      return true
    }

    const newFieldValues = { ...fieldValues, ...data }

    if (stepOrder[step] === 'details' && newFieldValues.dateOfBirth)
      newFieldValues.age = moment().diff(
        moment(newFieldValues.dateOfBirth, 'DD/MM/YYYY'),
        'years',
      )

    if (data.email && fieldValues.email !== data.email)
      setEmailValidated(false)

    setFieldValues(newFieldValues)
    // sendDataForCurrentStep()
  }

  const validateEmail = () => {
    setFetchState({ ...fetchState, loading: true, error: null })
    wordpressPost(
      'model-application/v1/validate',
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          email: fieldValues.email,
        }),
      },
      fieldValues.office,
    )
      .then(resp => {
        if (resp.error) {
          setFetchState({
            ...fetchState,
            error: resp.error,
            loading: false,
          })
        } else if (resp === true) {
          setEmailValidated(true)
          setFetchState({
            ...fetchState,
            loading: false,
          })
        } else {
          setFetchState({
            ...fetchState,
            error: 'Unknown error please contact us for assistance',
            loading: false,
          })
          Sentry.captureException(new Error('Email validation failed'))
          // console.log('There was an email validation error, resp:', resp)
        }
      })
      .catch(err => {
        setFetchState({
          ...fetchState,
          error: 'Unknown error please contact us for assistance',
          loading: false,
        })
        Sentry.captureException(err)
        // console.log(
        //   '[Caught] There was an email validation error, resp:',
        //   err
        // )
      })
  }

  const sendApplication = () => {
    setFetchState({ ...fetchState, loading: true, error: null })

    const body = {
      ...fieldValues,
      first_name: fieldValues.firstName,
      last_name: fieldValues.surname,
      telephone_number: fieldValues.telephoneNumber,
      date_of_birth: fieldValues.dateOfBirth,
      guardian_first_name: fieldValues.guardianFirstName,
      guardian_last_name: fieldValues.guardianSurname,
      guardian_email: fieldValues.guardianEmail,
      guardian_image: fieldValues.guardianPhoto
        ? fieldValues.guardianPhoto.data
        : null,
      guardian_consent: fieldValues.guardianConsent,
      application_type: fieldValues.applicationType,
      representation_name: fieldValues.representationName,
      suit_size: fieldValues.suitSize,
      suit_cut: fieldValues.suitCut,
      shoe_size: fieldValues.shoeSize,
      hair_colour: fieldValues.hairColour,
      eye_colour: fieldValues.eyeColour,
      image_full_figure: fieldValues.fullFigure.data,
      image_full_figure_meta: fieldValues.fullFigure.meta,
      image_portrait: fieldValues.portrait.data,
      image_portrait_meta: fieldValues.portrait.meta,
      image_side_on: fieldValues.sideOn.data,
      image_side_on_meta: fieldValues.sideOn.meta,
      image_head_and_shoulders: fieldValues.headAndShoulders.data,
      image_head_and_shoulders_meta: fieldValues.headAndShoulders.meta,
      terms_agreed: fieldValues.termsAgreed,
    }

    Sentry.addBreadcrumb({
      category: 'application',
      message: `Submitting application for ${fieldValues.firstName} ${fieldValues.surname}`,
      level: 'debug',
      data: body,
    })

    wordpressPost(
      'model-application/v1/new',
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      },
      fieldValues.office,
    )
      .then(resp => {
        if (resp.error) {
          setFetchState({ ...fetchState, error: resp.error, loading: false })
          Sentry.addBreadcrumb({
            category: 'application',
            message: `Application submission error: ${resp.error}`,
            level: 'error',
            data: {error: resp.error, data: resp.data},
          })
          Sentry.captureException(new Error('Application submission failed'))
        } else if (resp === true) {
          ReactPixel.track('SubmitApplication')
          setFetchState({ ...fetchState, loading: false })
          setComplete(true)
        } else {
          setFetchState({
            ...fetchState,
            error: 'Unknown error please contact us for assistance',
            loading: false,
          })
          // console.log('There was a form submission error, resp:', resp)
        }
      })
      .catch(err => {
        setFetchState({
          ...fetchState,
          error: 'Unknown error please contact us for assistance',
          loading: false,
        })
        console.log(
          '[Caught] There was an email validation error, resp:',
          err,
        )
        Sentry.captureException(err)
      })
  }

  const sharedProps = {
    goBack: () => {
      nextStep(step - 1)
      let newStep = step - 1
      if (
        newStep === stepOrder.indexOf('guardianConsent') &&
        fieldValues.age >= 16
      )
        newStep -= 1
      setStep(newStep)
    },
    onSubmit: handleSubmit,
    initialValues: fieldValues,
  }

  return (
    <StateInitializer globalSettings={globalSettings} sites={sites} siteSlug={siteSlug}>
      <ApplyLayout className={s.layout} activeArea='form'>
        {(() => {
          switch (step) {
            case 7:
              return (
                <ApplyComplete
                  title='Thank you for applying!'
                  siteSlug={fieldValues.office}
                />
              )
            case 6:
              return <ApplyFormConsent {...sharedProps} />
            case 5:
              return <ApplyFormSocial {...sharedProps} />
            case 4:
              return <ApplyFormImages {...sharedProps} />
            case 3:
              return <ApplyFormMeasurements {...sharedProps} />
            case 2:
              return <ApplyFormApplicationType {...sharedProps} />
            case 1:
              return <ApplyFormGuardianConsent {...sharedProps} />
            case 0:
            default:
              return <ApplyFormDetails {...sharedProps} />
          }
        })()}
        {(fetchState.loading || fetchState.error) && (
          <Overlay
            close={() => {
              setFetchState({
                ...fetchState,
                error: false,
              })
            }}
          >
            <div className={s.overlay}>
              {fetchState.loading && (
                <>
                  <svg
                    width='44'
                    height='44'
                    viewBox='0 0 44 44'
                    xmlns='http://www.w3.org/2000/svg'
                    stroke='#2e2e2e'
                    className={s.loadingIcon}
                  >
                    <g fill='none' fillRule='evenodd' strokeWidth='2'>
                      <circle cx='22' cy='22' r='1'>
                        <animate
                          attributeName='r'
                          begin='0s'
                          dur='1.8s'
                          values='1; 20'
                          calcMode='spline'
                          keyTimes='0; 1'
                          keySplines='0.165, 0.84, 0.44, 1'
                          repeatCount='indefinite'
                        />
                        <animate
                          attributeName='stroke-opacity'
                          begin='0s'
                          dur='1.8s'
                          values='1; 0'
                          calcMode='spline'
                          keyTimes='0; 1'
                          keySplines='0.3, 0.61, 0.355, 1'
                          repeatCount='indefinite'
                        />
                      </circle>
                      <circle cx='22' cy='22' r='1'>
                        <animate
                          attributeName='r'
                          begin='-0.9s'
                          dur='1.8s'
                          values='1; 20'
                          calcMode='spline'
                          keyTimes='0; 1'
                          keySplines='0.165, 0.84, 0.44, 1'
                          repeatCount='indefinite'
                        />
                        <animate
                          attributeName='stroke-opacity'
                          begin='-0.9s'
                          dur='1.8s'
                          values='1; 0'
                          calcMode='spline'
                          keyTimes='0; 1'
                          keySplines='0.3, 0.61, 0.355, 1'
                          repeatCount='indefinite'
                        />
                      </circle>
                    </g>
                  </svg>
                  <h3 className={s.overlayTitle}>
                    {!emailValidated
                      ? 'Validating your email'
                      : 'Processing your details...'}
                  </h3>
                </>
              )}
              {fetchState.error && (
                <>
                  <h3 className={s.overlayTitle}>Sorry, something went wrong</h3>
                  <p
                    className={s.overlayError}
                    dangerouslySetInnerHTML={{ __html: fetchState.error }}
                  />
                  <p className={s.overlayIntro}>
                    Please refresh the page and try again, if you continue to
                    recieve this error please contact us for assistance
                  </p>
                </>
              )}
            </div>
          </Overlay>
        )}
      </ApplyLayout>
    </StateInitializer>
  )
}

export default Start

export async function getStaticProps({ params }) {
  const apolloClient = createApolloClient()
  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(),
    getSites(),
    apolloClient.query({
      query: APPLY_QUERY,
      context: { site: 'WP_ROOT' },
    }),
  ])

  if (error) console.log(`Error loading Apply props - ${error}`)

  return {
    props: {
      ...params,
      ...data.pageBy.acfApplySettings,
      globalSettings,
      sites,
      siteSlug: null,
    },
    revalidate: 60 * 60 * 24, // Revalidate every 24 hours
  }
}
